import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DefaultOptionType } from "antd/es/select";
import { isEqual } from "lodash-es";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import Region from "@mapmycustomers/shared/types/Region";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";

import { useConfigProvider } from "../../../../ConfigProvider";
import { InlineSelectFieldProps } from "../../../inline/SelectField";

import useAutoDetectCountry from "./useAutoDetectCountry";

interface UseManualAddressStateOptions {
  autoDetectCountry?: boolean;
  onChange?: (address?: Address) => void;
  onReverseGeocodeAddress: (payload: {
    callback: (result: GeocodeResult) => void;
    coordinates: LongLat;
    failureCallback?: () => void;
  }) => void;
  value?: Address;
}

const useManualAddressState = ({
  autoDetectCountry,
  onChange,
  onReverseGeocodeAddress,
  value,
}: UseManualAddressStateOptions) => {
  const { useFindMyLocation } = useConfigProvider();
  const [address, setAddress] = useState(value);
  const [detectedCountry, setDetectedCountry] =
    useState<Pick<Address, "country" | "countryCode">>();

  const resolvedAddress = useMemo(
    () => (detectedCountry ? ({ ...value, ...detectedCountry } as Address) : value),
    [detectedCountry, value]
  );
  const isAddressChanged = !isEqual(address, resolvedAddress);

  const handleAddressChange = useCallback(
    (nextAddress: string) =>
      setAddress((currentAddress) => ({
        ...currentAddress,
        address: nextAddress.length > 0 ? nextAddress : undefined,
      })),
    []
  );

  const handleCountryCodeChange = useCallback<
    NonNullable<InlineSelectFieldProps<CountryCode>["onChange"]>
  >((countryCode, option) => {
    setAddress((currentAddress) => ({
      ...currentAddress,
      country: option ? (option as DefaultOptionType).text : null,
      countryCode: countryCode ?? null,
      region: undefined,
      regionCode: undefined,
    }));
  }, []);

  const handleCityChange = useCallback(
    (city: string) =>
      setAddress((currentAddress) => ({
        ...currentAddress,
        city: city.length > 0 ? city : undefined,
      })),
    []
  );

  const handleRegionChange = useCallback(
    (region: Region) => setAddress((currentAddress) => ({ ...currentAddress, ...region })),
    []
  );

  const handlePostalCodeChange = useCallback(
    (postalCode: string) =>
      setAddress((currentAddress) => ({
        ...currentAddress,
        postalCode: postalCode.length > 0 ? postalCode : undefined,
      })),
    []
  );

  const handleSave = useCallback(() => {
    onChange?.(address);
  }, [address, onChange]);

  const handleCancel = useCallback(() => {
    setAddress(resolvedAddress);
  }, [resolvedAddress]);

  const [, , findMeLoading, , handleFindMyLocation, geocodingResult, resetError] =
    useFindMyLocation(onReverseGeocodeAddress);
  const lastGeocodingResult = useRef<GeocodeResult>();

  const canClearCountry = useMemo(
    () =>
      !isEmptyString(value?.countryCode) &&
      isEmptyString(value?.address) &&
      isEmptyString(value?.city) &&
      isEmptyString(value?.region) &&
      isEmptyString(value?.postalCode),
    [value]
  );

  useEffect(() => {
    resetError();
  }, [resetError, value]);

  useEffect(() => {
    if (lastGeocodingResult.current !== geocodingResult) {
      lastGeocodingResult.current = geocodingResult;
      onChange?.(geocodingResult?.address);
      setAddress(geocodingResult?.address);
    }
  }, [geocodingResult, onChange]);

  const handleCountryDetected = useCallback((country: string, countryCode: CountryCode) => {
    setDetectedCountry({ country, countryCode });
    setAddress((currentAddress) => ({ ...currentAddress, country, countryCode }));
  }, []);

  useEffect(() => {
    if (!isEmptyString(value?.countryCode)) {
      setDetectedCountry(undefined);
    }
  }, [value?.countryCode]);

  useAutoDetectCountry({
    enabled: !!autoDetectCountry,
    hasCountry: !isEmptyString(value?.countryCode),
    onCountryDetected: handleCountryDetected,
    onReverseGeocodeAddress,
  });

  return {
    address,
    canClearCountry,
    findMeLoading,
    handleAddressChange,
    handleCancel,
    handleCityChange,
    handleCountryCodeChange,
    handleFindMyLocation,
    handlePostalCodeChange,
    handleRegionChange,
    handleSave,
    isAddressChanged,
  };
};

export default useManualAddressState;
